import React, { useState } from 'react';
import {
  Box, Grid, Typography, Card, CardContent, CardActions,
  IconButton, Select, MenuItem, TextField, Button, Table,
  TableBody, TableCell, TableHead, TableRow, InputAdornment,
  Snackbar, Alert, FormControl, InputLabel, CircularProgress, Divider,
} from '@mui/material';
import { Add, Remove, Delete, QrCodeScanner } from '@mui/icons-material';
import { useGetProductsQuery, useGetCustomersQuery, useCreateOrderMutation } from 'state/api';
import { PageContainer, formatCurrency, BarcodeScanner } from 'components/shared';
import ProductDetailsModal from './productdeatilsmodal';

const PAYMENT_METHODS = ['cash', 'card', 'upi', 'netbanking'];

const POS = () => {
  const [cart, setCart] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paidAmount, setPaidAmount] = useState('');
  const [discount, setDiscount] = useState('');
  const [tax, setTax] = useState('');
  const [search, setSearch] = useState('');
  const [barcode, setBarcode] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const { data: productData, isLoading: productsLoading } = useGetProductsQuery({ limit: 100, search });
  const { data: customerData } = useGetCustomersQuery({ limit: 100 });
  const [createOrder, { isLoading: ordering }] = useCreateOrderMutation();

  const products = productData?.data || [];
  const customers = customerData?.data || [];

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const total = subtotal - (parseFloat(discount) || 0) + (parseFloat(tax) || 0);
  const change = (parseFloat(paidAmount) || 0) - total;

  const notify = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const addToCart = (product) => {
    const price = product.sellingPrice ?? product.price ?? 0;
    setCart((prev) => {
      const existing = prev.find((i) => i._id === product._id);
      if (existing) return prev.map((i) => i._id === product._id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { _id: product._id, name: product.name, price, quantity: 1 }];
    });
    notify(`${product.name} added`);
  };

  const updateQuantity = (id, delta) => {
    setCart((prev) =>
      prev.map((i) => i._id === id ? { ...i, quantity: Math.max(i.quantity + delta, 1) } : i)
    );
  };

  const removeFromCart = (id) => setCart((prev) => prev.filter((i) => i._id !== id));

  const handleAddByBarcode = () => {
    const q = barcode.trim();
    const product = products.find(
      (p) => p.barcode === q || p.productCode === q || p._id === q
    );
    if (product) { addToCart(product); setBarcode(''); }
    else notify('Product not found', 'error');
  };

  // Called when the camera scanner reads a code.
  const handleScanned = (code) => {
    const q = (code || '').trim();
    if (!q) return;
    const product = products.find(
      (p) => p.barcode === q || p.productCode === q || p._id === q
    );
    if (product) {
      addToCart(product);
    } else {
      // Not in the loaded page — search the catalog by the scanned code.
      setSearch(q);
      notify(`Scanned ${q} — searching catalog…`, 'info');
    }
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) { notify('Cart is empty', 'error'); return; }
    if (!customerId) { notify('Please select a customer', 'error'); return; }
    try {
      await createOrder({
        customer: customerId,
        items: cart.map((i) => ({ product: i._id, price: i.price, quantity: i.quantity })),
        paymentMethod,
        totalDiscount: parseFloat(discount) || 0,
        total,
      }).unwrap();
      notify('Order placed successfully!');
      setCart([]);
      setCustomerId('');
      setPaidAmount('');
      setDiscount('');
      setTax('');
    } catch (err) {
      notify(err?.data?.message || 'Failed to place order', 'error');
    }
  };

  return (
    <PageContainer
      title="Point of Sale"
      subtitle="Add products to the cart and complete checkout"
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        {/* Product Section — scrollable catalog */}
        <Box sx={{ flexGrow: 1, width: '100%', minWidth: 0 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Products</Typography>
              <TextField
                fullWidth
                placeholder="Search by name or SKU"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Enter or scan barcode, then Enter"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddByBarcode()}
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="end"><Button onClick={handleAddByBarcode}>Add</Button></InputAdornment>,
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<QrCodeScanner />}
                  onClick={() => setScannerOpen(true)}
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  Scan
                </Button>
              </Box>
              {productsLoading ? (
                <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
              ) : (
                <Box sx={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto', pr: 0.5 }}>
                  <Grid container spacing={1.5}>
                    {products.map((product) => (
                      <Grid item xs={6} sm={4} md={3} key={product._id}>
                        <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <CardContent sx={{ pb: 0, cursor: 'pointer', flexGrow: 1 }} onClick={() => setSelectedProduct(product)}>
                            <Typography variant="body2" noWrap>{product.name}</Typography>
                            <Typography variant="subtitle1" fontWeight="bold">{formatCurrency(product.sellingPrice ?? product.price)}</Typography>
                            <Typography variant="caption" color="text.secondary">Stock: {product.stock}</Typography>
                          </CardContent>
                          <CardActions>
                            <Button size="small" variant="contained" onClick={() => addToCart(product)} disabled={product.stock === 0}>
                              {product.stock === 0 ? 'Out of Stock' : 'Add'}
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Billing Section — sticky cart / checkout panel */}
        <Box
          sx={{
            width: { xs: '100%', md: 400 },
            flexShrink: 0,
            position: { md: 'sticky' },
            top: 16,
            alignSelf: 'flex-start',
            maxHeight: { md: 'calc(100vh - 100px)' },
            overflowY: { md: 'auto' },
          }}
        >
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Billing</Typography>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Customer</InputLabel>
                <Select value={customerId} label="Customer" onChange={(e) => setCustomerId(e.target.value)}>
                  {customers.map((c) => (
                    <MenuItem key={c._id} value={c._id}>{c.name} — {c.phoneNo}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cart.map((item) => (
                    <TableRow key={item._id}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => updateQuantity(item._id, -1)}><Remove fontSize="small" /></IconButton>
                        {item.quantity}
                        <IconButton size="small" onClick={() => updateQuantity(item._id, 1)}><Add fontSize="small" /></IconButton>
                      </TableCell>
                      <TableCell>{formatCurrency(item.price * item.quantity)}</TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => removeFromCart(item._id)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography>Subtotal: {formatCurrency(subtotal)}</Typography>
                <TextField fullWidth label="Discount (₹)" type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} sx={{ my: 1 }} size="small" />
                <TextField fullWidth label="Tax (₹)" type="number" value={tax} onChange={(e) => setTax(e.target.value)} sx={{ mb: 1 }} size="small" />
                <Typography variant="h6">Total: {formatCurrency(total)}</Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Payment Method</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {PAYMENT_METHODS.map((m) => (
                    <Button key={m} size="small" variant={paymentMethod === m ? 'contained' : 'outlined'} onClick={() => setPaymentMethod(m)}>
                      {m.toUpperCase()}
                    </Button>
                  ))}
                </Box>
                <TextField fullWidth label="Paid Amount (₹)" type="number" value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)} size="small" />
                {paidAmount && <Typography variant="body2" sx={{ mt: 0.5 }}>Change: {formatCurrency(change)}</Typography>}
              </Box>

              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button variant="outlined" color="error" onClick={() => { setCart([]); notify('Order cancelled', 'warning'); }}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={handlePlaceOrder} disabled={ordering || cart.length === 0}>
                  {ordering ? <CircularProgress size={20} color="inherit" /> : 'Place Order'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {selectedProduct && (
        <ProductDetailsModal
          open={Boolean(selectedProduct)}
          onClose={() => setSelectedProduct(null)}
          product={selectedProduct}
          onAddToCart={addToCart}
        />
      )}

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={handleScanned}
        title="Scan product barcode"
      />

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default POS;
